import { loadStripe, Stripe } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;
const environment = clientToken?.startsWith("pk_test_") ? "sandbox" : "live";

// Log which Stripe publishable key the current build was compiled against so
// it's obvious in the browser console whether checkouts will hit the test
// sandbox or the live account. Only the key prefix + last 4 chars are logged
// — publishable keys are safe to expose, but truncating keeps logs tidy.
//
// We also POST the (mode, key_tail, build_mode, pathname) to the
// `log-stripe-config` edge function so admins can audit configuration drift
// over time. Fire-and-forget, deduped per browser session via sessionStorage.
if (typeof window !== "undefined") {
  const mode = environment === "live" ? "LIVE" : "TEST";
  const tokenPreview = clientToken
    ? `${clientToken.slice(0, 8)}…${clientToken.slice(-4)}`
    : "(missing)";
  const buildMode = import.meta.env.MODE;
  // eslint-disable-next-line no-console
  console.info(
    `%c[Stripe] ${mode} mode`,
    environment === "live"
      ? "color:#059669;font-weight:bold"
      : "color:#ea580c;font-weight:bold",
    `— publishable key ${tokenPreview} (build: ${buildMode})`,
  );

  // Audit ping — one row per browser session per (mode, keyTail) combo so we
  // don't spam the table on every route change or HMR reload. Failures are
  // swallowed because this is purely an audit signal.
  try {
    const keyTail = clientToken ? clientToken.slice(-6) : "??????";
    const auditMode = clientToken?.startsWith("pk_live_")
      ? "live"
      : clientToken?.startsWith("pk_test_")
        ? "sandbox"
        : "unknown";
    const dedupeKey = `stripe-config-audit:${auditMode}:${keyTail}`;
    if (!window.sessionStorage.getItem(dedupeKey)) {
      window.sessionStorage.setItem(dedupeKey, String(Date.now()));
      void supabase.functions
        .invoke("log-stripe-config", {
          body: {
            mode: auditMode,
            keyTail,
            buildMode,
            pathname: window.location.pathname,
          },
        })
        .catch(() => {
          /* audit logging is best-effort */
        });
    }
  } catch {
    /* sessionStorage unavailable — skip audit ping */
  }
}

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    if (!clientToken) {
      throw new Error("VITE_PAYMENTS_CLIENT_TOKEN is not set");
    }
    stripePromise = loadStripe(clientToken);
  }
  return stripePromise;
}

export async function getStripePriceId(priceId: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke("get-stripe-price", {
    body: { priceId, environment },
  });
  if (error || !data?.stripeId) {
    throw new Error(`Failed to resolve price: ${priceId}`);
  }
  return data.stripeId as string;
}

export function getStripeEnvironment(): "sandbox" | "live" {
  return environment;
}

export const PRICE_IDS = {
  standard: "standard",
  pro: "pro",
} as const;
